/* eslint-disable react/no-did-update-set-state */
/* eslint-disable react/no-did-mount-set-state */
/* eslint-disable react/no-string-refs */

// node modules
import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import {
  SearchBox,
  SimpleQueryString,
  BoolShould,
  BoolMust,
  TermQuery,
  PaginationAccessor,
} from 'searchkit';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import omit from 'lodash/omit';
import foldToAscii from 'fold-to-ascii';

import { sendEvent } from 'services/analytics';
import formatter from 'services/formatter';

// services
import history from 'services/history';

// constants
import { SEARCH_INTENT_STATE, TOUCHED_FILTERS_STATE_KEY } from 'constants/search';

// assets
import CssSearchBoxCustom from './SearchBoxCustom.scss';

// How long typing must have stopped before the results are expected to match the box.
const RESULTS_SETTLE_MS = 2000;

class SearchBoxCustom extends SearchBox {
  static defaultProps = Object.assign({}, SearchBox.defaultProps, {
    queryBuilder: (query, options) => {
      // In order to boost exact matches add an additional search for a simplified version of the query
      // with all spaces, punctuation and negative terms removed.
      const fullTitleQuery = foldToAscii
        .fold(query)
        .toLowerCase()
        .replace(/[^\w\d]*-[\w\d]+/g, '') // Remove negative terms (like: -cheese)
        .replace(/[^\w\d]/g, ''); // Remove all punctuation

      const queryDsl = BoolShould([
        SimpleQueryString(`${query.trim()}`, options),
        // Exact matches should come in above all other results
        // boost weights will be added by the searchProxy
        {
          term: {
            full_title: {
              value: fullTitleQuery,
            },
          },
        },
        {
          term: {
            full_subtitle: {
              value: fullTitleQuery,
            },
          },
        },
      ]);

      if (query.match(/\brecipes?\b/)) {
        return BoolMust([queryDsl, TermQuery('label.raw', 'recipe')]);
      }

      return queryDsl;
    },
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
    progressClassName: 'sk-search-box__progress--light',
    disabled: false,
    autoFocus: true,
  });

  static dismissVirtualKeyboard() {
    if (
      document.activeElement &&
      document.activeElement.blur &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur();
    }
  }

  constructor(props) {
    super(props);
    this.state.zoomed = false;

    // Results follow the keystrokes, but the URL does not. Every distinct URL is
    // a browser history record - Chrome keeps it in the store behind the omnibox
    // even when the write replaces the back-stack entry rather than adding one -
    // so the URL is written when the search is committed, not while it is being
    // typed. An idle timer cannot stand in for that: anyone typing slower than
    // the timer gets a record per keystroke again.
    this.throttledSearch = throttle(() => {
      this.searchQuery(this.accessor.getQueryString(), false);
      this.openSearchViewIfNeeded();
    }, props.searchThrottleTime);

    this.debouncedReportDesync = debounce(() => this.reportDesync(), RESULTS_SETTLE_MS);

    this.bound = {
      onSubmit: this.onSubmit.bind(this),
      onChange: this.onInputChange.bind(this),
      onFocus: this.onFocus.bind(this),
      onBlur: this.onBlur.bind(this),
      onClick: this.onClick.bind(this),
      onIconClick: this.onIconClick.bind(this),
    };

    this.queryFieldRef = React.createRef();
  }

  onFocus(event) {
    const { onFocus } = this.props;
    this.setFocusState(true);
    onFocus(event);
  }

  onBlur(event) {
    const { onBlur } = this.props;
    this.setFocusState(false);
    onBlur(event);
    this.setState({ zoomed: false });
    // Leaving the field commits whatever was typed into it.
    this.settleUrl();
  }

  onSubmit(event) {
    event.preventDefault();
    this.throttledSearch.cancel();
    // Submitting is an explicit search gesture, so the saved diet Limit may seed
    // it - but a universalLink flag carried over would veto that intent, and a filter marker
    // carried over would make a search the reader has never filtered behave as if they had.
    this.searchkit.pendingLocationState = {
      ...omit(history.location.state, ['universalLink', TOUCHED_FILTERS_STATE_KEY]),
      ...SEARCH_INTENT_STATE,
    };
    this.searchQuery(this.getValue() || 'recipes');
    SearchBoxCustom.dismissVirtualKeyboard();
  }

  onChange(event) {
    const { onChange } = this.props;
    const newEvent = {
      target: {
        value: formatter.removeSmartCharacters(event.target.value),
      },
    };

    // Reset pagination when the query changes
    const paginationAccessor = this.searchkit.getAccessorByType(PaginationAccessor);
    if (paginationAccessor) {
      paginationAccessor.resetState();
    }

    super.onChange(newEvent);
    onChange(newEvent);
    this.setState({ zoomed: true });
  }

  onInputChange(event) {
    this.searchkit.queryBeingTyped = true;
    this.onChange(event);
    this.debouncedReportDesync();
  }

  onClick() {
    this.setState({ zoomed: true });
  }

  onIconClick() {
    this.throttledSearch.cancel();
    history.push(`/search?q=${encodeURIComponent(this.getValue())}`, { ...SEARCH_INTENT_STATE });
  }

  /**
   * Whether the URL the search is about to write is the one already on screen.
   * @return {Boolean}
   */
  isOnSearchRoute() {
    return history.location.pathname === this.searchkit.options.getLocation().pathname;
  }

  /**
   * Typing somewhere other than the search view is what takes the user to it, and
   * that navigation earns one history entry. It carries no query: the search is
   * still being typed, and the entry is settled in place once it is committed.
   */
  openSearchViewIfNeeded() {
    if (this.isOnSearchRoute()) {
      return;
    }
    this.searchkit.openSearchView();
  }

  /**
   * Commits the typed query to the URL, so it can be refreshed, copied and
   * navigated back to. Flushing the throttle first means the query being written
   * is the one that was searched for.
   */
  settleUrl() {
    this.throttledSearch.flush();
    this.searchkit.writeHistory(this.isOnSearchRoute());
  }

  /**
   * Reports the box and the results still disagreeing once typing and searching have stopped,
   * which should never happen. A failed search is reported separately as Search.Error.
   */
  reportDesync() {
    const { loading, error, results, resultsQueryString } = this.searchkit;
    if (loading) {
      this.debouncedReportDesync();
      return;
    }
    const query = this.accessor.getQueryString() || '';
    if (!error && results && query !== (resultsQueryString || '')) {
      sendEvent({
        event: 'Search.Desync',
        query,
        resultsQuery: resultsQueryString || '',
      });
    }
  }

  searchQuery(query, updateHistory = true) {
    const shouldResetOtherState = false;
    this.accessor.setQueryString(query, shouldResetOtherState);
    this.searchkit.performSearch(this.isOnSearchRoute(), undefined, updateHistory);
    if (this.searchkit.accessors.statefulAccessors.label) {
      this.searchkit.accessors.statefulAccessors.label.state.value = [];
    }
    sendEvent({
      event: 'Search.Type',
      query,
    });
  }

  componentDidMount() {
    super.componentDidMount();
    const { focussed } = this.props;
    const element = this.queryFieldRef.current;
    if (focussed && element) {
      element.focus();
      this.setState({ zoomed: true });
    }
    if (element) {
      const { value } = element;
      if (value) {
        this.onChange({ target: element });
      }
    }
  }

  componentWillUnmount() {
    this.throttledSearch.cancel();
    this.debouncedReportDesync.cancel();
    super.componentWillUnmount();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.focussed && this.props.focussed) {
      const element = this.queryFieldRef.current;
      if (element) {
        this.setState({ zoomed: true });
        setTimeout(() => {
          if (this.queryFieldRef.current) {
            this.queryFieldRef.current.focus();

            // If we came in from the Home page Hero, its search box click handler will have created a dummy input
            // to help make sure iOS Safari displays the keyboard when focus occurs. We should remove that element
            // and hopefully the keyboard should remain open.
            const focusHelper = document.getElementById('search-box-focus-helper');
            if (focusHelper) {
              focusHelper.remove();
            }
          }
        }, 300);
      }
    }
  }

  render() {
    const { disabled, autoFocus } = this.props;
    const { focused, zoomed } = this.state;
    const block = this.bemBlocks.container;

    let placeholder = this.props.placeholder || this.translate('searchbox.placeholder');
    if (zoomed) {
      placeholder = '';
    }
    return (
      <div className={block().state({ focused })}>
        <form onSubmit={this.bound.onSubmit} action=".">
          <div
            className={`search__icon ${block('icon')}`}
            onClick={this.bound.onIconClick}
            onKeyPress={this.bound.onIconClick}
            role="button"
            tabIndex="0"
            aria-label="Search"
          >
            <svg fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              <path d="M0 0h24v24H0z" fill="none" />
            </svg>
          </div>
          <div className={block('wrapper')}>
            <input
              type="search"
              data-qa="query"
              className={`${this.props.className} ${block('text')}`}
              placeholder={placeholder}
              value={formatter.removeSmartCharacters(this.accessor ? this.getValue() : '')}
              onFocus={this.bound.onFocus}
              onBlur={this.bound.onBlur}
              onClick={this.bound.onClick}
              ref={this.queryFieldRef}
              autoFocus={autoFocus}
              autoCapitalize="off"
              onChange={this.bound.onChange}
              disabled={disabled}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              data-cy="searchbox"
            />
            <input
              type="submit"
              value={this.translate('searchbox.button')}
              className={block('action')}
              data-qa="submit"
            />
            <div className={`${this.props.progressClassName} ${block('progress')}`}>
              <div
                data-qa="loader"
                className={block('loader')
                  .mix('sk-spinning-loader')
                  .state({ hidden: !this.isLoading() })}
              />
            </div>
          </div>
        </form>
      </div>
    );
  }
}

export default withStyles(CssSearchBoxCustom)(SearchBoxCustom);
